// =============================================================================
// TournamentTab.js
// /var/www/binko-frontend/src/pages/game/TournamentTab.js
//
// All tournament UI states in one component.
// Polls GET /api/tournament/state every 1s.
// Phases: IDLE, SIGNUP, ROUND_*, *_RESULT, BREAK_*, WINNER, CANCELLED
// =============================================================================

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Typography, Button, LinearProgress, CircularProgress, Chip } from '@mui/material';
import api from '../services/api';
import { C } from '../theme';
import { IconTrophy, IconMedal, IconCorrect, IconWrong, IconNoAnswer, IconBolt, IconTick } from '../BinkoIcons';

// ---------------------------------------------------------------------------
// Countdown helper — mm:ss from seconds
// ---------------------------------------------------------------------------
function fmt(sec) {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Phase label map
// ---------------------------------------------------------------------------
const PHASE_LABEL = {
  ROUND_1: 'Round 1',
  ROUND_2: 'Round 2',
  SEMI:    'Semi-final',
  FINAL:   'Final',
  SUDDEN_DEATH: 'Sudden Death',
};

const RESULT_PHASES = new Set([
  'ROUND_1_RESULT','ROUND_2_RESULT','SEMI_RESULT',
  'FINAL_RESULT','SUDDEN_DEATH_RESULT'
]);

const BREAK_PHASES = new Set(['BREAK_1','BREAK_2','BREAK_3']);

const LEADERBOARD_PHASES = new Set([
  'ROUND_1_LEADERBOARD','ROUND_2_LEADERBOARD','SEMI_LEADERBOARD',
  'FINAL_LEADERBOARD','SUDDEN_DEATH_LEADERBOARD'
]);

const QUESTION_PHASES = new Set([
  'ROUND_1','ROUND_2','SEMI','FINAL','SUDDEN_DEATH'
]);

// Questions per phase — mirrors QUESTIONS_PER_PHASE in tournamentLoop.js
const PHASE_Q_COUNT = {
  ROUND_1: 10,
  ROUND_2:  8,
  SEMI:     4,
  FINAL:    3,
  SUDDEN_DEATH: 3,
};

// Scoring per phase — mirrors lines 177-178 in tournamentLoop.js
const PHASE_SCORE = {
  ROUND_1:      { correct: 10, speed: 5 },
  ROUND_2:      { correct: 10, speed: 5 },
  SEMI:         { correct: 20, speed: 10 },
  FINAL:        { correct: 20, speed: 10 },
  SUDDEN_DEATH: { correct: 20, speed: 10 },
};

// Advancing text shown on per-question leaderboard and break screens
const PHASE_ADVANCING = {
  ROUND_1:      'All players advance to Round 2',
  ROUND_2:      'Top 5 advance to Semi-final',
  SEMI:         'Top 3 advance to Final',
  FINAL:        'Top scorer wins the tournament 🏆',
  SUDDEN_DEATH: 'Sudden Death — first to pull ahead wins',
};

// Standard ranking (1,2,2,4) from leaderboard array sorted by score DESC
function computeRanks(lb) {
  const ranks = [];
  let rank = 1;
  for (let i = 0; i < lb.length; i++) {
    if (i > 0 && lb[i].total_score < lb[i - 1].total_score) {
      rank = i + 1;
    }
    ranks.push(rank);
  }
  return ranks;
}

function rankLabel(rank) {
  if (rank === 1) return <IconMedal rank={1} size={20} />;
  if (rank === 2) return <IconMedal rank={2} size={20} />;
  if (rank === 3) return <IconMedal rank={3} size={20} />;
  return '#' + rank;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function TournamentTab({ venueId }) {
  const [state,       setState]     = useState(null);
  const [myAnswer,    setMyAnswer]  = useState(null);
  const [submitting,  setSubmitting]= useState(false);
  const pollRef  = useRef(null);
  const prevPhase = useRef(null);

  const poll = useCallback(async () => {
    if (!venueId) return;
    try {
      const r = await api.get('/tournament/state', { params: { venue_id: venueId } });
      const s = r.data;
      // Reset answer on new question phase
      if (QUESTION_PHASES.has(s.phase) && s.phase !== prevPhase.current) {
        setMyAnswer(null);
      }
      prevPhase.current = s.phase;
      setState(s);
    } catch (_) {}
  }, [venueId]);

  useEffect(() => {
    poll();
    pollRef.current = setInterval(poll, 1000);
    return () => clearInterval(pollRef.current);
  }, [poll]);

  async function joinTournament() {
    try {
      await api.post('/tournament/join', { venue_id: venueId });
      poll();
    } catch (_) {}
  }

  async function submitAnswer(letter) {
    if (myAnswer || submitting || !state) return;
    setMyAnswer(letter);
    setSubmitting(true);
    try {
      await api.post('/tournament/answer', {
        session_id:     state.session_id,
        phase:          state.phase,
        question_index: state.current_question_index,
        answer:         letter
      });
    } catch (_) {}
    finally { setSubmitting(false); }
  }

  if (!state) {
    return (
      <Box sx={S.center}>
        <CircularProgress size={28} sx={{ color: C.yellow }} />
      </Box>
    );
  }

  // Route to correct view
  const phase = state.phase;

  if (phase === 'IDLE')      return <IdleView state={state} />;
  if (phase === 'SIGNUP')    return <SignupView state={state} onJoin={joinTournament} />;
  if (phase === 'CANCELLED') return <CancelledView state={state} />;
  if (phase === 'WINNER')    return <WinnerView state={state} venueId={venueId} onJoin={joinTournament} />;

  if (BREAK_PHASES.has(phase))      return <BreakView state={state} />;
  if (RESULT_PHASES.has(phase))     return <ResultView state={state} myAnswer={myAnswer} />;
  if (LEADERBOARD_PHASES.has(phase)) return <TournamentLeaderboardView state={state} />;
  if (QUESTION_PHASES.has(phase)) return (
    <QuestionView
      state={state}
      myAnswer={myAnswer}
      onAnswer={submitAnswer}
    />
  );

  // Fallback
  return <IdleView state={state} />;
}

// ---------------------------------------------------------------------------
// IDLE — no active tournament, show next time
// ---------------------------------------------------------------------------
function getScheduleMessage(upcoming) {
  if (!upcoming) return { line1: 'Tournaments from 6 PM tonight', line2: 'Rounds every 30 min · Signup opens 5 min before' };
  const start = new Date(upcoming.scheduled_start);
  const now = new Date();
  const timeStr = start.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();
  const isToday = start.toDateString() === now.toDateString();
  if (isToday) {
    return {
      line1: 'Tournament tonight at ' + timeStr,
      line2: 'Rounds every 30 min · Signup opens 5 min before'
    };
  }
  return {
    line1: 'Next tournament tomorrow at ' + timeStr,
    line2: 'Rounds every 30 min from 6:00 PM'
  };
}

function IdleView({ state }) {
  const up = state.upcoming;
  const msg = getScheduleMessage(up);
  return (
    <Box sx={S.center}>
      <Box sx={{ mb: 1 }}><IconTrophy size={48} /></Box>
      <Typography sx={S.title}>Tournament</Typography>
      <Typography sx={{ ...S.sub, mt: 1 }}>{msg.line1}</Typography>
      {msg.line2 && (
        <Typography sx={{ color: C.textDisabled, fontSize: '0.78rem', mt: 0.75, textAlign: 'center' }}>
          {msg.line2}
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// SIGNUP — countdown + join button
// ---------------------------------------------------------------------------
function SignupView({ state, onJoin }) {
  const [secs, setSecs] = useState(state.seconds_to_start);

  useEffect(() => {
    setSecs(state.seconds_to_start);
  }, [state.seconds_to_start]);

  useEffect(() => {
    const t = setInterval(() => setSecs(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <Box sx={S.center}>
      <Box sx={{ mb: 1 }}><IconTrophy size={44} /></Box>
      <Typography sx={S.title}>Tournament</Typography>
      <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '2rem', my: 1 }}>
        {secs > 0 ? fmt(secs) : 'Starting…'}
      </Typography>
      <Typography sx={S.sub}>
        {state.player_count} player{state.player_count !== 1 ? 's' : ''} joined · {state.min_players} required to start
      </Typography>
      {state.player_count >= 2 && state.player_count < state.min_players && state.seconds_to_start <= 60 && (
        <Typography sx={{ color: C.orange, fontSize: '0.8rem', mt: 0.75, textAlign: 'center' }}>
          Starting as Unofficial Game — no trophies awarded
        </Typography>
      )}

      {state.has_joined ? (
        <Box sx={S.joinedBox}>
          <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.95rem' }}>
            ✓ You're in!
          </Typography>
          <Typography sx={{ color: C.textMuted, fontSize: '0.8rem', mt: 0.5 }}>
            {secs > 0 ? `Tournament starts in ${fmt(secs)}` : 'Starting…'}
          </Typography>
        </Box>
      ) : (
        <Button sx={S.joinBtn} onClick={onJoin}>
          Join Tournament
        </Button>
      )}

      <Typography sx={{ color: C.textDisabled, fontSize: '0.75rem', mt: 2, textAlign: 'center' }}>
        Your Binko alias will be used automatically
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// QUESTION — active question with timer
// ---------------------------------------------------------------------------
function QuestionView({ state, myAnswer, onAnswer }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 250);
    return () => clearInterval(t);
  }, []);

  const q = state.current_question;
  if (!q) return <Box sx={S.center}><CircularProgress size={24} sx={{ color: C.yellow }} /></Box>;

  const phaseDuration = state.phase_duration_ms;
  const phaseStart    = state.phase_start_at;

  function timeRemaining() {
    if (!phaseStart || !phaseDuration) return 0;
    const rem = phaseStart + 11000 - Date.now();
    return Math.max(0, Math.ceil(rem / 1000));
  }

  function progressPct() {
    if (!phaseStart) return 100;
    const elapsed = Date.now() - phaseStart;
    return Math.max(0, Math.min(100, 100 - (elapsed / 11000) * 100));
  }

  const tRem    = timeRemaining();
  const pPct    = progressPct();
  const urgent  = tRem <= 3;
  const phLabel = PHASE_LABEL[state.phase] || state.phase;
  const qNum    = (state.current_question_index ?? 0) + 1;
  const qTotal  = PHASE_Q_COUNT[state.phase] || '?';

  return (
    <Box sx={S.phaseBox}>
      {/* Meta row */}
      <Box sx={S.metaRow}>
        <Chip label={phLabel} size="small"
          sx={{ background: 'rgba(251,188,21,0.12)', color: C.yellow, fontSize: '0.7rem' }} />
        <Typography sx={{ color: C.textDisabled, fontSize: '0.8rem' }}>
          Q{qNum} of {qTotal}
        </Typography>
        <Typography sx={{ color: C.teal, fontSize: '0.8rem', fontWeight: 600 }}>
          {state.my_score ?? 0} pts
        </Typography>
      </Box>

      {/* Timer */}
      <Box sx={S.timerRow}>
        <LinearProgress variant="determinate" value={pPct} sx={{
          ...S.timerBar,
          '& .MuiLinearProgress-bar': { background: urgent ? C.orange : C.yellow, transition: 'none' }
        }} />
        <Typography sx={{ ...S.timerNum, color: urgent ? C.orange : C.textPrimary }}>
          {tRem}s
        </Typography>
      </Box>

      <Typography sx={S.questionText}>{q.question}</Typography>

      <Box sx={S.optionsGrid}>
        {['A','B','C','D'].map(letter => {
          const selected = myAnswer === letter;
          return (
            <Button key={letter} disabled={!!myAnswer}
              onClick={() => onAnswer(letter)}
              sx={{
                ...S.optionBtn,
                ...(selected ? S.optionSelected : {}),
                ...(myAnswer && !selected ? S.optionDimmed : {})
              }}>
              <span style={{ fontWeight: 700, marginRight: 8 }}>{letter}</span>
              {q.options[letter]}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// RESULT — show correct answer + leaderboard snapshot
// ---------------------------------------------------------------------------
function ResultView({ state, myAnswer }) {
  const q         = state.current_question;
  const lb        = state.leaderboard || [];
  const myResult  = state.my_result || null;
  // Use server my_result — myAnswer resets to null before RESULT phase renders
  const isCorrectAnswer = myResult ? myResult.is_correct === 1 : false;
  const noAnswer        = !myResult;
  // Derive scoring from phase — matches tournamentLoop.js lines 177-178
  const basePhase   = state.phase.replace('_RESULT', '');
  const scoring     = PHASE_SCORE[basePhase] || PHASE_SCORE.ROUND_1;
  const earned      = myResult ? myResult.score_earned : 0;
  const hadSpeed    = isCorrectAnswer && earned > scoring.correct;
  const speedEarned = hadSpeed ? earned - scoring.correct : 0;

  return (
    <Box sx={S.phaseBox}>
      {/* Correct / wrong / no answer icon */}
      <Box sx={{ mb: 1 }}>
        {noAnswer ? <IconNoAnswer size={48} /> : isCorrectAnswer ? <IconCorrect size={48} /> : <IconWrong size={48} />}
      </Box>

      {/* Question text */}
      {q && (
        <Typography sx={S.questionText}>{q.question}</Typography>
      )}

      {/* Answer options with correct highlighted */}
      {q && (
        <Box sx={{ width: '100%', mb: 2 }}>
          {['A','B','C','D'].map(letter => {
            const isCorrect = letter === q.correct;
            const wasMine   = myAnswer === letter;
            return (
              <Box key={letter} sx={{
                ...S.resultOption,
                ...(isCorrect ? S.resultCorrect : {}),
                ...(wasMine && !isCorrect ? S.resultWrong : {})
              }}>
                <span style={{ fontWeight: 700, marginRight: 8 }}>{letter}</span>
                {q.options[letter]}
                {isCorrect && <span style={{ marginLeft: 'auto' }}>✓</span>}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Score breakdown */}
      <Box sx={S.scoreBreakdown}>
        {isCorrectAnswer ? (
          <Typography sx={{ color: C.success, fontWeight: 700, fontSize: '0.95rem', textAlign: 'center' }}>
            +{scoring.correct} pts{hadSpeed ? <> <IconBolt size={12} style={{ verticalAlign: 'middle', margin: '0 2px' }} />+{speedEarned} speed bonus</> : ''}
          </Typography>
        ) : (
          <Typography sx={{ color: C.textMuted, fontSize: '0.9rem', textAlign: 'center' }}>
            {noAnswer ? 'No answer submitted' : 'Not quite — better luck next round!'}
          </Typography>
        )}
      </Box>

      {/* How many got it right */}
      {state.correct_count !== undefined && (
        <Typography sx={{ color: C.textMuted, fontSize: '0.8rem', mt: 1.5, mb: 1 }}>
          {state.correct_count} of {state.total_answered} players got it right
        </Typography>
      )}

    </Box>
  );
}

// ---------------------------------------------------------------------------
// BREAK — leaderboard + who advances
// ---------------------------------------------------------------------------
function BreakView({ state }) {
  const lb = state.leaderboard || [];
  const ranks = computeRanks(lb);

  const nextPhase = state.phase === 'BREAK_1' ? 'ROUND_2'
    : state.phase === 'BREAK_2' ? 'SEMI'
    : 'FINAL';

  const phLabel = state.phase === 'BREAK_1' ? 'After Round 1'
    : state.phase === 'BREAK_2' ? 'After Round 2'
    : 'After Semi-final';

  const nextLabel  = PHASE_LABEL[nextPhase] || nextPhase;
  const scoring    = PHASE_SCORE[nextPhase]    || PHASE_SCORE.ROUND_1;
  const qCount     = PHASE_Q_COUNT[nextPhase]  || '?';
  const advancing  = PHASE_ADVANCING[nextPhase] || '';

  // Cut count: how many players advance (null = all advance)
  const cutCount = state.phase === 'BREAK_2' ? 5
    : state.phase === 'BREAK_3' ? 3
    : null;

  // Index of last advancing player (for cut line placement)
  let cutLineAfterIndex = null;
  if (cutCount) {
    for (let i = ranks.length - 1; i >= 0; i--) {
      if (ranks[i] <= cutCount) { cutLineAfterIndex = i; break; }
    }
  }

  const [secsLeft, setSecsLeft] = React.useState(() => {
    if (!state.phase_start_at || !state.phase_duration_ms) return 0;
    return Math.max(0, Math.ceil((state.phase_start_at + state.phase_duration_ms - Date.now()) / 1000));
  });

  React.useEffect(() => {
    const t = setInterval(() => {
      if (!state.phase_start_at || !state.phase_duration_ms) return;
      setSecsLeft(Math.max(0, Math.ceil((state.phase_start_at + state.phase_duration_ms - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(t);
  }, [state.phase_start_at, state.phase_duration_ms]);

  return (
    <Box sx={S.phaseBox}>
      <Typography sx={S.title}>{nextLabel} Begins</Typography>
      <Typography sx={{ color: C.textMuted, fontSize: '0.82rem', mb: 0.5 }}>
        {phLabel}
      </Typography>

      {/* Countdown */}
      <Typography sx={{ color: C.textMuted, fontSize: '0.8rem', mt: 0.5 }}>
        Starts in
      </Typography>
      <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '2rem', mb: 1.5 }}>
        {fmt(secsLeft)}
      </Typography>

      {/* Scoring rules for upcoming round */}
      <Box sx={S.rulesBox}>
        <Typography sx={{ ...S.rulesText, fontWeight: 700, color: C.textPrimary, mb: 0.5 }}>
          {nextLabel} · {qCount} questions
        </Typography>
        <Typography sx={S.rulesText}>
          <IconCorrect size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} />{scoring.correct} pts correct · <IconBolt size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} />+{scoring.speed} speed bonus (&lt;5s)
        </Typography>
        <Typography sx={{ ...S.rulesText, mt: 0.25, color: C.teal }}>
          {advancing}
        </Typography>
      </Box>

      {/* Leaderboard */}
      <Box sx={{ width: '100%', mt: 1 }}>
        {lb.map((p, i) => {
          const eliminated     = p.status === 'ELIMINATED';
          const showCutLine    = cutLineAfterIndex !== null && i === cutLineAfterIndex && i < lb.length - 1;
          return (
            <React.Fragment key={p.user_id || i}>
              <Box sx={{
                ...S.lbRow,
                ...(eliminated ? { opacity: 0.38 } : {}),
                ...(p.is_winner ? S.lbRowWinner : {})
              }}>
                <Typography sx={S.lbRank}>{rankLabel(ranks[i])}</Typography>
                <Typography sx={S.lbAlias}>{p.display_name}</Typography>
                {eliminated && (
                  <Chip label="Out" size="small"
                    sx={{ fontSize: '0.65rem', height: 18, background: 'rgba(254,114,16,0.12)',
                      color: C.orange, mr: 1 }} />
                )}
                <Typography sx={S.lbScore}>{p.total_score}</Typography>
              </Box>
              {showCutLine && (
                <Box sx={{ borderTop: `1px dashed ${C.orange}`, my: 0.5,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: C.orange, fontSize: '0.7rem', py: 0.25 }}>
                    ── elimination line ──
                  </Typography>
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// WINNER
// ---------------------------------------------------------------------------
function WinnerView({ state, venueId, onJoin }) {
  const lb      = state.leaderboard || [];
  const ranks   = computeRanks(lb);
  const winners = lb.filter(p => p.is_winner);
  const top3    = lb.slice(0, 3);
  const up      = state.upcoming;

  // Countdown to next slot
  const [secsToNext, setSecsToNext] = useState(() => {
    if (!up) return null;
    return Math.max(0, Math.ceil((new Date(up.scheduled_start) - Date.now()) / 1000));
  });

  useEffect(() => {
    if (!up) return;
    const t = setInterval(() => {
      const rem = Math.max(0, Math.ceil((new Date(up.scheduled_start) - Date.now()) / 1000));
      setSecsToNext(rem);
    }, 1000);
    return () => clearInterval(t);
  }, [up]);

  const isSignupOpen = up && up.status === 'SIGNUP';

  return (
    <Box sx={S.center}>
      <Box sx={{ mb: 1 }}><IconTrophy size={52} /></Box>
      <Typography sx={S.title}>
        {winners.length > 1 ? 'Joint Winners!' : state.via_sudden_death ? 'Sudden Death Winner!' : 'Winner!'}
      </Typography>
      {winners.map(w => (
        <Typography key={w.user_id}
          sx={{ color: C.yellow, fontWeight: 700, fontSize: '1.3rem', mt: 0.25 }}>
          {w.display_name}
        </Typography>
      ))}
      <Typography sx={{ ...S.sub, mt: 1 }}>
        {state.is_official
          ? (winners.length > 1 ? 'Both earn a trophy 🏆' : 'Earns a trophy 🏆')
          : 'Unofficial game — no trophies awarded'}
      </Typography>

      {/* Top 3 podium */}
      <Box sx={{ mt: 3, width: '100%' }}>
        <Typography sx={{ color: C.textDisabled, fontSize: '0.75rem', mb: 1 }}>
          Final standings
        </Typography>
        {top3.map((p, i) => (
          <Box key={p.user_id || i} sx={{
            ...S.lbRow,
            ...(p.is_winner ? S.lbRowWinner : {})
          }}>
            <Typography sx={S.lbRank}>{rankLabel(ranks[i])}</Typography>
            <Typography sx={S.lbAlias}>{p.display_name}</Typography>
            {p.is_winner && <IconTrophy size={16} style={{ marginRight: 4, flexShrink: 0 }} />}
            <Typography sx={S.lbScore}>{p.total_score}</Typography>
          </Box>
        ))}
      </Box>

      {/* Next round info + register */}
      {up && (
        <Box sx={{
          mt: 3, width: '100%', borderTop: '1px solid ' + C.border,
          pt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center'
        }}>
          {secsToNext !== null && secsToNext > 0 ? (
            <>
              <Typography sx={{ color: C.textMuted, fontSize: '0.82rem', mb: 0.5 }}>
                Next round starts in
              </Typography>
              <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '1.6rem', mb: 1.5 }}>
                {fmt(secsToNext)}
              </Typography>
            </>
          ) : (
            <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.95rem', mb: 1.5 }}>
              Next round starting soon
            </Typography>
          )}
          {isSignupOpen ? (
            <Button sx={S.joinBtn} onClick={onJoin}>
              Register for Next Round
            </Button>
          ) : (
            <Typography sx={{ color: C.textDisabled, fontSize: '0.78rem', textAlign: 'center' }}>
              Registration opens 5 min before start
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// CANCELLED
// ---------------------------------------------------------------------------
function CancelledView({ state }) {
  const up = state.upcoming;
  return (
    <Box sx={S.center}>
      <Typography sx={{ fontSize: '2rem', mb: 1 }}>😔</Typography>
      <Typography sx={S.title}>Not enough players</Typography>
      <Typography sx={S.sub}>Need at least 2 players to start</Typography>
      {up && (
        <Typography sx={{ color: C.yellow, fontWeight: 600, mt: 2, fontSize: '0.95rem' }}>
          Next tournament in {up.minutes_away} min
        </Typography>
      )}
      <Typography sx={{ ...S.sub, mt: 2 }}>
        In the meantime, try the Trivia tab →
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Mini leaderboard (shared by ResultView + WinnerView)
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// LEADERBOARD phase — per-question standings (mirrors trivia LeaderboardPhaseView)
// ---------------------------------------------------------------------------
function TournamentLeaderboardView({ state }) {
  const lb          = state.leaderboard || [];
  const ranks       = computeRanks(lb);
  const basePhase   = state.phase.replace('_LEADERBOARD', '');
  const phLabel     = PHASE_LABEL[basePhase] || 'Round';
  const qNum        = (state.current_question_index ?? 0) + 1;
  const qTotal      = PHASE_Q_COUNT[basePhase] || '?';
  const scoring     = PHASE_SCORE[basePhase]   || PHASE_SCORE.ROUND_1;
  const advancing   = PHASE_ADVANCING[basePhase] || '';

  return (
    <Box sx={S.phaseBox}>
      <Typography sx={S.title}>
        {phLabel} · Q{qNum} of {qTotal}
      </Typography>

      {/* Scoring rules + advancing */}
      <Box sx={S.rulesBox}>
        <Typography sx={S.rulesText}>
          <IconCorrect size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} />{scoring.correct} pts correct · <IconBolt size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} />+{scoring.speed} speed bonus (&lt;5s)
        </Typography>
        <Typography sx={{ ...S.rulesText, mt: 0.25, color: C.teal }}>
          {advancing}
        </Typography>
      </Box>

      <Box sx={{ width: '100%', mt: 1 }}>
        {lb.map((p, i) => {
          const eliminated = p.status === 'ELIMINATED';
          return (
            <Box key={p.user_id || i} sx={{
              ...S.lbRow,
              ...(eliminated ? { opacity: 0.38 } : {}),
              ...(p.is_me ? { background: 'rgba(251,188,21,0.08)', border: '1px solid rgba(251,188,21,0.25)' } : {})
            }}>
              <Typography sx={S.lbRank}>{rankLabel(ranks[i])}</Typography>
              <Typography sx={S.lbAlias}>{p.display_name}</Typography>
              {eliminated && (
                <Chip label="Out" size="small"
                  sx={{ fontSize: '0.65rem', height: 18, background: 'rgba(254,114,16,0.12)',
                    color: C.orange, mr: 1 }} />
              )}
              <Typography sx={S.lbScore}>{p.total_score}</Typography>
            </Box>
          );
        })}
      </Box>
      <Typography sx={{ ...S.sub, mt: 2 }}>Next question coming up…</Typography>
    </Box>
  );
}

function MiniLeaderboard({ rows, myScore, showTrophy }) {
  return (
    <Box sx={{ width: '100%' }}>
      {rows.map((p, i) => (
        <Box key={p.user_id || i} sx={{
          ...S.lbRow,
          ...(p.is_winner ? S.lbRowWinner : {})
        }}>
          <Typography sx={S.lbRank}>
            {i === 0 ? <IconMedal rank={1} size={20} /> : i === 1 ? <IconMedal rank={2} size={20} /> : i === 2 ? <IconMedal rank={3} size={20} /> : `#${i + 1}`}
          </Typography>
          <Typography sx={S.lbAlias}>{p.display_name}</Typography>
          {showTrophy && p.is_winner && (
            <IconTrophy size={16} style={{ marginRight: 4, flexShrink: 0 }} />
          )}
          <Typography sx={S.lbScore}>{p.total_score}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const S = {
  center: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    width: '100%', pt: 4, pb: 4, px: 1
  },
  phaseBox: {
    width: '100%', display: 'flex', flexDirection: 'column',
    alignItems: 'center', pt: 1, pb: 4
  },
  title: { color: C.textPrimary, fontWeight: 700, fontSize: '1.2rem', mb: 0.5, textAlign: 'center' },
  sub:   { color: C.textMuted, fontSize: '0.88rem', textAlign: 'center' },
  joinBtn: {
    background: C.yellow, color: C.yellowText, fontWeight: 700,
    borderRadius: 5, px: 4, py: 1.2, mt: 2,
    '&:hover': { background: '#e6aa10' }
  },
  joinedBox: {
    mt: 2, background: 'rgba(251,188,21,0.1)', border: `1px solid rgba(251,188,21,0.3)`,
    borderRadius: 3, px: 4, py: 1.5, textAlign: 'center'
  },
  metaRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    width: '100%', mb: 1
  },
  timerRow: { display: 'flex', alignItems: 'center', width: '100%', gap: 1, mb: 2 },
  timerBar: { flexGrow: 1, height: 8, borderRadius: 4, background: C.bgElevated },
  timerNum: { fontWeight: 700, minWidth: 30, textAlign: 'right', fontSize: '0.9rem' },
  questionText: {
    color: C.textPrimary, fontSize: '1.05rem', fontWeight: 600,
    textAlign: 'center', mb: 2, lineHeight: 1.5, width: '100%'
  },
  scoreBreakdown: {
    background: C.bgCard, borderRadius: 2, px: 2, py: 1.5, width: '100%', mt: 1, textAlign: 'center'
  },
  optionsGrid:  { display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' },
  optionBtn: {
    background: C.bgCard, color: C.textMuted, border: `1px solid ${C.border}`,
    borderRadius: 2, py: 1.5, px: 2, textAlign: 'left',
    justifyContent: 'flex-start', textTransform: 'none', fontSize: '0.9rem',
    '&:hover': { background: C.bgElevated, borderColor: C.yellow }
  },
  optionSelected: { background: 'rgba(251,188,21,0.12)', borderColor: C.yellow, color: C.yellow },
  optionDimmed:   { opacity: 0.4 },
  resultOption: {
    display: 'flex', alignItems: 'center',
    background: C.bgCard, color: C.textMuted,
    border: `1px solid ${C.border}`, borderRadius: 2,
    py: 1.5, px: 2, fontSize: '0.9rem', width: '100%', mb: 1
  },
  resultCorrect: { background: 'rgba(34,197,94,0.1)', color: C.success, borderColor: C.success },
  resultWrong:   { background: 'rgba(254,114,16,0.1)', color: C.orange, borderColor: C.orange },
  lbRow: {
    display: 'flex', alignItems: 'center', gap: 1,
    background: C.bgCard, borderRadius: 2, px: 1.5, py: 1, mb: 0.75
  },
  lbRowWinner: { background: 'rgba(251,188,21,0.08)', border: `1px solid rgba(251,188,21,0.25)` },
  lbRank:  { minWidth: 32, fontSize: '0.9rem', fontWeight: 700, color: C.textPrimary },
  lbAlias: { flexGrow: 1, color: C.textMuted, fontSize: '0.9rem' },
  lbScore: { color: C.yellow, fontWeight: 700, fontSize: '0.9rem' },
  rulesBox: {
    background: C.bgCard, border: `1px solid ${C.border}`,
    borderRadius: 2, px: 2, py: 1.25, width: '100%', mb: 1, textAlign: 'center'
  },
  rulesText: { color: C.textMuted, fontSize: '0.8rem' },
};
